// GDL90 receiver source — Wave 2 STUB.
//
// What this is: the pathway for receiver-based ADS-B traffic. The
// architecture is in place so when we ship Wave 2 (UDP listener over
// WiFi or a Capacitor-native socket bridge), THIS is the only file
// that needs to change. The traffic store, map renderer, detail card,
// and source priority resolver are all already source-agnostic.
//
// Why it isn't wired yet:
//   Browsers cannot open raw UDP sockets — required to receive GDL90
//   frames over WiFi port 4000 from Stratus, Sentry, Stratux, Garmin
//   GDL portables, etc. Two options for the future:
//     a) Capacitor / native shell wrapping iOS/Android socket APIs.
//     b) A small local bridge service (Node / Go) running on the same
//        WiFi network that re-broadcasts via WebSocket.
//   See docs/avionics-integration-research.md for the full protocol
//   details + which option to ship.
//
// What this file exports:
//   - Public API that mirrors internet.ts so the rest of the app can
//     refer to a "GDL90 source" symmetrically.
//   - A `pushGdl90Targets()` helper that takes a parsed batch and
//     pipes it into useTrafficStore — wireable from a Capacitor
//     plugin or a WebSocket client whenever those exist.
//   - A status checker `isGdl90Available()` that returns false in v1
//     so the UI status pill renders correctly.

import { useTrafficStore } from '../store';
import type { TrafficTarget } from '../types';

let _started = false;
let _bridgeWs: WebSocket | null = null;
let _bridgeUrl: string | null = null;

/**
 * Mount the GDL90 source. v1 is a no-op (no native socket or bridge
 * available). Future: connects to a local bridge WebSocket if one is
 * configured, or invokes the Capacitor plugin in a native build.
 */
export function startGdl90Traffic(): void {
  if (_started) return;
  _started = true;
  // Future v2 wiring:
  //   1. If running under Capacitor, register a listener with the
  //      `fyrefly-gdl90` plugin that emits parsed Traffic Reports
  //      (msg 20) + Ownship (msg 10) + AHRS (msg 76). Pipe Traffic
  //      Reports through pushGdl90Targets().
  //   2. Else, if a bridge URL is configured in settings, open a
  //      WebSocket to it (e.g. ws://192.168.10.1:4001/gdl90) and
  //      handle the same message stream.
  //   3. Else stay silent — internet source carries the load.
  if (_bridgeUrl) attemptBridgeConnect(_bridgeUrl);
}

export function disposeGdl90Traffic(): void {
  _started = false;
  if (_bridgeWs) {
    try { _bridgeWs.close(); } catch { /* ignore */ }
    _bridgeWs = null;
  }
}

/**
 * Configure (or clear) the local bridge URL. When set, a WebSocket
 * connection is opened and TrafficTarget batches are accepted as
 * JSON-encoded arrays. The bridge is responsible for parsing GDL90
 * frames into the wire shape.
 */
export function setGdl90BridgeUrl(url: string | null): void {
  _bridgeUrl = url;
  if (_bridgeWs) {
    try { _bridgeWs.close(); } catch { /* ignore */ }
    _bridgeWs = null;
  }
  if (_started && url) attemptBridgeConnect(url);
}

/** True when a GDL90 source is currently producing data. */
export function isGdl90Available(): boolean {
  return useTrafficStore.getState().sourceStatus.gdl90.active;
}

/**
 * Public push API for Wave 2 / native bridges. A Capacitor plugin or
 * a WebSocket bridge can call this with a TrafficTarget[] batch any
 * time a GDL90 Traffic Report frame arrives. The store handles
 * priority resolution; the receiver wins over internet automatically.
 */
export function pushGdl90Targets(batch: TrafficTarget[]): void {
  if (!_started) return;
  // Defensive: tag source field even if the caller forgot.
  const tagged = batch.map((t) => ({ ...t, source: 'gdl90' as const }));
  useTrafficStore.getState().pushTargets('gdl90', tagged);
}

/* -------------------- internal --------------------- */

function attemptBridgeConnect(url: string): void {
  if (typeof WebSocket === 'undefined') return;
  try {
    const ws = new WebSocket(url);
    _bridgeWs = ws;
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(String(ev.data));
        if (Array.isArray(data)) {
          pushGdl90Targets(data as TrafficTarget[]);
        } else if (data && typeof data === 'object' && Array.isArray(data.targets)) {
          pushGdl90Targets(data.targets as TrafficTarget[]);
        }
      } catch {
        // Bridge sent something we don't understand. Drop silently.
      }
    };
    ws.onclose = () => {
      if (_bridgeWs === ws) _bridgeWs = null;
      // Retry connect after a delay if still mounted with the same URL.
      if (_started && _bridgeUrl === url) {
        setTimeout(() => attemptBridgeConnect(url), 5_000);
      }
    };
    ws.onerror = () => {
      useTrafficStore
        .getState()
        .reportSourceError('gdl90', `bridge socket error (${url})`);
    };
  } catch (err) {
    useTrafficStore
      .getState()
      .reportSourceError(
        'gdl90',
        err instanceof Error ? err.message : String(err),
      );
  }
}
